import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Dumbbell, ArrowLeft, Lock, Sparkles, Trash2, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SPORTS = ['running', 'swimming', 'strength_home', 'strength_gym', 'cycling'];

const L = {
  es: {
    title: 'Planes de entrenamiento', back: 'Volver a Entrenamiento',
    premiumOnly: 'Solo para usuarios Premium.', upgrade: 'Mejorar a Premium',
    noProfile: 'Completa primero tu perfil de entrenamiento.', goProfile: 'Ir al perfil',
    generate: 'Generar nuevo plan', name: 'Nombre (opcional)', namePh: 'Ej: Mesociclo fuerza',
    weeks: 'Duración (semanas)', days: 'Días por semana', alternate: 'Alternar días en semanas pares',
    altDays: 'Días en semanas alternas', start: 'Fecha de inicio', sports: 'Deportes a incluir',
    create: 'Generar plan', creating: 'Generando plan...', myPlans: 'Mis planes', empty: 'Aún no tienes planes.',
    open: 'Abrir', weeksLabel: 'semanas', quota: 'Generaciones este mes',
    sportLabels: { running: 'Correr', swimming: 'Nadar', strength_home: 'Fuerza en casa', strength_gym: 'Fuerza en gym', cycling: 'Ciclismo' },
    goals: { fat_loss: 'Perder grasa', muscle_gain: 'Ganar músculo', performance: 'Rendimiento', maintenance: 'Mantenimiento' },
    deleteConfirm: '¿Borrar este plan?',
  },
  en: {
    title: 'Training plans', back: 'Back to Training',
    premiumOnly: 'Premium users only.', upgrade: 'Upgrade to Premium',
    noProfile: 'Complete your training profile first.', goProfile: 'Go to profile',
    generate: 'Generate new plan', name: 'Name (optional)', namePh: 'E.g.: Strength mesocycle',
    weeks: 'Duration (weeks)', days: 'Days per week', alternate: 'Alternate days on even weeks',
    altDays: 'Days on alternating weeks', start: 'Start date', sports: 'Sports to include',
    create: 'Generate plan', creating: 'Generating plan...', myPlans: 'My plans', empty: 'No plans yet.',
    open: 'Open', weeksLabel: 'weeks', quota: 'Generations this month',
    sportLabels: { running: 'Running', swimming: 'Swimming', strength_home: 'Strength at home', strength_gym: 'Strength at gym', cycling: 'Cycling' },
    goals: { fat_loss: 'Fat loss', muscle_gain: 'Muscle gain', performance: 'Performance', maintenance: 'Maintenance' },
    deleteConfirm: 'Delete this plan?',
  },
};

export default function TrainingPlansPage() {
  const { language } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const t = L[language] || L.es;
  const isPremium = user?.plan === 'premium';

  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [quota, setQuota] = useState(null);
  const [profile, setProfile] = useState(null);
  const [generating, setGenerating] = useState(false);

  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    name: '', weeks: 4, days_per_week: 4, alternate: false, days_per_week_alt: 3,
    start_date: today, sports: [],
  });

  useEffect(() => {
    if (!isPremium) { setLoading(false); return; }
    Promise.all([fetchPlans(), fetchProfile()]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPremium]);

  const fetchPlans = async () => {
    try {
      const res = await fetch(`${API}/training/plans`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setPlans(d.plans || []); setQuota(d.quota || null); }
    } catch (e) { console.error(e); }
  };

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API}/training/profile`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setProfile(d.profile || null);
        if (d.profile?.sports?.length) setForm(f => ({ ...f, sports: d.profile.sports }));
      }
    } catch (e) { console.error(e); }
  };

  const toggleSport = (s) => setForm(f => ({ ...f, sports: f.sports.includes(s) ? f.sports.filter(x => x !== s) : [...f.sports, s] }));

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/training/plans/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          name: form.name || null,
          weeks: parseInt(form.weeks),
          days_per_week: parseInt(form.days_per_week),
          days_per_week_alt: form.alternate ? parseInt(form.days_per_week_alt) : null,
          start_date: form.start_date,
          sports: form.sports,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(language === 'es' ? 'Plan generado' : 'Plan generated');
        navigate(`/training/plans/${data.plan_id}`);
      } else {
        toast.error(data.detail || (language === 'es' ? 'No se pudo generar' : 'Could not generate'));
      }
    } catch { toast.error(language === 'es' ? 'Error de conexión' : 'Connection error'); }
    finally { setGenerating(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t.deleteConfirm)) return;
    try {
      const res = await fetch(`${API}/training/plans/${id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { setPlans(prev => prev.filter(p => p.plan_id !== id)); }
    } catch { toast.error(language === 'es' ? 'Error' : 'Error'); }
  };

  if (loading) {
    return <Layout><div className="max-w-4xl mx-auto px-4 py-12 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;
  }

  if (!isPremium) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-12 text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4"><Lock className="w-8 h-8 text-primary" /></div>
          <h1 className="font-manrope font-bold text-2xl text-charcoal mb-2">{t.title}</h1>
          <p className="text-stone font-inter mb-6">{t.premiumOnly}</p>
          <Button onClick={() => navigate('/pricing')} className="rounded-full bg-primary">{t.upgrade}</Button>
        </div>
      </Layout>
    );
  }

  const profileReady = profile && profile.sex && profile.age && profile.height_cm && profile.weight_kg && profile.goal;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/training')} className="flex items-center gap-2 text-stone hover:text-primary mb-4 font-inter">
          <ArrowLeft className="w-4 h-4" /> {t.back}
        </button>
        <div className="flex items-center gap-3 mb-6">
          <Dumbbell className="w-7 h-7 text-primary" />
          <h1 className="font-manrope font-bold text-3xl text-charcoal">{t.title}</h1>
        </div>

        {!profileReady ? (
          <Card className="bg-white rounded-3xl shadow-soft p-6 text-center">
            <p className="text-stone font-inter mb-4">{t.noProfile}</p>
            <Button onClick={() => navigate('/training')} className="rounded-full bg-primary">{t.goProfile}</Button>
          </Card>
        ) : (
          <Card className="bg-white rounded-3xl shadow-soft p-6 space-y-4 mb-8">
            <h2 className="font-manrope font-bold text-lg text-charcoal">{t.generate}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-sm text-stone">{t.name}</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t.namePh} className="h-11 rounded-xl border-border bg-white/50" />
              </div>
              <div className="space-y-1">
                <Label className="text-sm text-stone">{t.start}</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="h-11 rounded-xl border-border bg-white/50" />
              </div>
              <div className="space-y-1">
                <Label className="text-sm text-stone">{t.weeks}</Label>
                <select value={form.weeks} onChange={(e) => setForm({ ...form, weeks: e.target.value })} className="h-11 w-full rounded-xl border border-border bg-white px-3 text-sm">
                  {[4, 6, 8, 12].map(w => <option key={w} value={w}>{w}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-sm text-stone">{t.days}</Label>
                <Input type="number" min="1" max="7" value={form.days_per_week} onChange={(e) => setForm({ ...form, days_per_week: e.target.value })} className="h-11 rounded-xl border-border bg-white/50" />
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.alternate} onChange={(e) => setForm({ ...form, alternate: e.target.checked })} className="w-4 h-4 accent-primary" />
                <span className="text-sm text-charcoal font-inter">{t.alternate}</span>
              </label>
              {form.alternate && (
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-stone">{t.altDays}:</Label>
                  <Input type="number" min="1" max="7" value={form.days_per_week_alt} onChange={(e) => setForm({ ...form, days_per_week_alt: e.target.value })} className="h-10 w-20 rounded-xl border-border bg-white/50" />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-stone">{t.sports}</Label>
              <div className="flex flex-wrap gap-2">
                {SPORTS.map(s => (
                  <button key={s} type="button" onClick={() => toggleSport(s)}
                    className={`px-4 py-2 rounded-full text-sm border-2 transition-all ${form.sports.includes(s) ? 'bg-primary text-white border-primary' : 'bg-white text-stone border-border hover:border-primary/50'}`}>
                    {t.sportLabels[s]}
                  </button>
                ))}
              </div>
            </div>
            {quota && quota.limit != null && (
              <p className="text-xs text-stone">{t.quota}: {quota.used} / {quota.limit}</p>
            )}
            <Button onClick={handleGenerate} disabled={generating} className="rounded-full bg-primary h-12 w-full font-manrope font-semibold">
              {generating ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Sparkles className="w-5 h-5 mr-2" />}
              {generating ? t.creating : t.create}
            </Button>
          </Card>
        )}

        <h2 className="font-manrope font-bold text-lg text-charcoal mb-3">{t.myPlans}</h2>
        {plans.length === 0 ? (
          <p className="text-stone italic font-inter">{t.empty}</p>
        ) : (
          <div className="space-y-3">
            {plans.map(p => (
              <Card key={p.plan_id} className="bg-white rounded-2xl shadow-soft p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"><Calendar className="w-6 h-6 text-primary" /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-manrope font-semibold text-charcoal truncate">{p.name}</p>
                  <p className="text-xs text-stone">{p.weeks} {t.weeksLabel} · {t.goals[p.goal] || p.goal} {p.status === 'archived' ? '· (archivado)' : ''}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate(`/training/plans/${p.plan_id}`)} className="rounded-full">{t.open}</Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(p.plan_id)} className="rounded-full text-destructive"><Trash2 className="w-4 h-4" /></Button>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
